import type {NonEmptyArray} from '@freckle/non-empty'
import type {ParserT} from '@freckle/parser'
import {
  record,
  array,
  mapStatic,
  nonEmptyArray,
  string,
  number,
  literal,
  firstOf
} from '@freckle/parser'
import type {SyncResultT} from '@freckle/educator-entities/ts/common/models/roster-sync'
import {parseSyncResult} from '@freckle/educator-entities/ts/common/models/roster-sync'

export type SyncCsvIdpDataArtifact =
  SyncCsvIdpDataArtifactFailedParsing | SyncCsvIdpDataArtifactSyncResults

type SyncCsvIdpDataArtifactFailedParsing = {
  tag: 'failed-parsing'
  contents: NonEmptyArray<ParseGroupedError>
}

type SyncCsvIdpDataArtifactSyncResults = {tag: 'sync-results'; contents: Array<SyncResultT>}

export type ParseGroupedError = GroupingError | ValidationErrors

type GroupingError = {tag: 'grouping-error'; contents: SisGroupingError}

type ValidationErrors = {
  tag: 'validation-errors'
  contents: NamedCsvException<SisCsvValidationError>[]
}

export type NamedCsvException<Err> = {
  csvFileName: string
  errors: Err[]
}

export type SisCsvValidationError =
  | DuplicateSisId
  | DuplicateEmail
  | SchoolNotFound
  | StudentNotEnrolled
  | UnsupportedLanguage
  | MalformedLanguage
  // Backend CSVExceptions are untagged JSON and mostly just contain an error
  // message. This is a catch all to ensure the parser doesn't break.
  | ErrorMessage

type DuplicateSisId = {
  tag: 'duplicate-sis-id'
  rowNumber: number
  rowNumberFirstSeen: number
}

type DuplicateEmail = {
  tag: 'duplicate-email'
  rowNumber: number
  rowNumberFirstSeen: number
}

type SchoolNotFound = {
  tag: 'school-not-found'
  rowNumber: number
}

type StudentNotEnrolled = {
  tag: 'student-not-enrolled'
  rowNumber: number
  studentId: string
}

type UnsupportedLanguage = {
  tag: 'unsupported-language'
  rowNumber: number
  studentId: string
}

type MalformedLanguage = {
  tag: 'malformed-language'
  rowNumber: number
  studentId: string
}

type ErrorMessage = {
  tag: 'error-message'
  message: string
}

export type SisGroupingError =
  EnrollmentStudentNotFound | EnrollmentCourseNotFound | CourseTeacherNotFound

type EnrollmentStudentNotFound = {
  tag: 'enrollment-student-not-found'
  contents: {
    studentSisId: string
  }
}

type EnrollmentCourseNotFound = {
  tag: 'enrollment-course-not-found'
  contents: {
    courseSisId: string
  }
}

type CourseTeacherNotFound = {
  tag: 'course-teacher-not-found'
  contents: {
    teacherEmail: string
    courseSisId: string
  }
}

const parseDuplicateSisId: ParserT<SisCsvValidationError> = record({
  tag: literal('duplicate-sis-id'),
  rowNumber: number(),
  rowNumberFirstSeen: number()
})

const parseDuplicateEmail: ParserT<SisCsvValidationError> = record({
  tag: literal('duplicate-email'),
  rowNumber: number(),
  rowNumberFirstSeen: number()
})

const parseSchoolNotFound: ParserT<SisCsvValidationError> = record({
  tag: literal('school-not-found'),
  rowNumber: number()
})

const parseStudentNotEnrolled: ParserT<SisCsvValidationError> = record({
  tag: literal('student-not-enrolled'),
  rowNumber: number(),
  studentId: string()
})

const parseUnsupportedLanguage: ParserT<SisCsvValidationError> = record({
  tag: literal('unsupported-language'),
  rowNumber: number(),
  studentId: string()
})

const parseMalformedLanguage: ParserT<SisCsvValidationError> = record({
  tag: literal('malformed-language'),
  rowNumber: number(),
  studentId: string()
})

const parseErrorMessage: ParserT<SisCsvValidationError> = mapStatic<
  {message: string},
  SisCsvValidationError
>(
  record({
    message: string()
  }),
  ({message}) => ({tag: 'error-message', message})
)

const parseValidationError: ParserT<SisCsvValidationError> = firstOf(
  parseDuplicateSisId,
  parseDuplicateEmail,
  parseSchoolNotFound,
  parseStudentNotEnrolled,
  parseUnsupportedLanguage,
  parseMalformedLanguage,
  parseErrorMessage
)

const parseEnrollmentStudentNotFoundContents: ParserT<{studentSisId: string}> = record({
  studentSisId: string()
})

const parseEnrollmentStudentNotFound: ParserT<SisGroupingError> = record({
  tag: literal('enrollment-student-not-found'),
  contents: parseEnrollmentStudentNotFoundContents
})

const parseEnrollmentCourseNotFoundContents: ParserT<{courseSisId: string}> = record({
  courseSisId: string()
})

const parseEnrollmentCourseNotFound: ParserT<SisGroupingError> = record({
  tag: literal('enrollment-course-not-found'),
  contents: parseEnrollmentCourseNotFoundContents
})

const parseCourseTeacherNotFoundContents: ParserT<{courseSisId: string; teacherEmail: string}> =
  record({
    courseSisId: string(),
    teacherEmail: string()
  })

const parseCourseTeacherNotFound: ParserT<SisGroupingError> = record({
  tag: literal('course-teacher-not-found'),
  contents: parseCourseTeacherNotFoundContents
})

const parseSisGroupingError: ParserT<SisGroupingError> = firstOf(
  parseEnrollmentStudentNotFound,
  parseEnrollmentCourseNotFound,
  parseCourseTeacherNotFound
)

const parseNamedValidationErrors: ParserT<NamedCsvException<SisCsvValidationError>> = record({
  csvFileName: string(),
  errors: array(parseValidationError)
})

const parseParseGroupedError: ParserT<ParseGroupedError> = firstOf(
  record({tag: literal('grouping-error'), contents: parseSisGroupingError}),
  record({tag: literal('validation-errors'), contents: array(parseNamedValidationErrors)})
)

export const parse: ParserT<SyncCsvIdpDataArtifact> = firstOf(
  record({tag: literal('failed-parsing'), contents: nonEmptyArray(parseParseGroupedError)}),
  record({tag: literal('sync-results'), contents: array(parseSyncResult)})
)
